import React, { useEffect, useState, useCallback } from 'react';
import { Clock, Plus, Edit2, Trash2, X, Search, AlertCircle, CheckCircle, DollarSign, Settings, ThumbsUp, ThumbsDown } from 'lucide-react';
import { getPengajuanLembur, createPengajuanLembur, updatePengajuanLembur, deletePengajuanLembur, approvePengajuanLembur, getLemburStats, getPengaturanLembur, updatePengaturanLembur, getKaryawan } from '../api';
import { useAuth } from '../AuthContext';

const emptyForm = { karyawan_id: '', tanggal: '', jam_mulai: '', jam_selesai: '', alasan: '', metode_hitung: 'manual', rate_per_jam: 0 };
const todayStr = () => new Date().toISOString().slice(0, 10);

function StatusBadge({ status }) {
  const s = (status || '');
  if (s === 'Disetujui') return <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>{s}</span>;
  if (s === 'Ditolak') return <span className="badge" style={{ background: '#fee2e2', color: '#991b1b' }}>{s}</span>;
  return <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>{s}</span>;
}

export default function PengajuanLembur() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({ metode_hitung: 'manual', rate_tetap: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedBulan, setSelectedBulan] = useState(todayStr().slice(0, 7));
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getPengajuanLembur({ bulan: selectedBulan }),
      getKaryawan(),
      getLemburStats({ bulan: selectedBulan }),
      getPengaturanLembur(),
    ])
      .then(([lemburRes, karRes, statsRes, settingsRes]) => {
        setData(lemburRes.data);
        setKaryawan(karRes.data.filter(k => k.status === 'Aktif'));
        setStats(statsRes.data);
        setSettings(settingsRes.data);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [selectedBulan]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.filter(d =>
    (!search || d.nama?.toLowerCase().includes(search.toLowerCase()) || d.nik?.toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || d.status === statusFilter)
  );

  // ===== CRUD =====
  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      tanggal: todayStr(),
      jam_mulai: '17:00',
      jam_selesai: '20:00',
      metode_hitung: settings?.metode_hitung || 'manual',
      rate_per_jam: settings?.rate_tetap || 0,
    });
    setError(''); setShowModal(true);
  };

  const openEdit = (item) => {
    if (item.status !== 'Menunggu') { alert('Pengajuan sudah diproses, tidak bisa diedit'); return; }
    setEditing(item);
    setForm({
      karyawan_id: item.karyawan_id,
      tanggal: item.tanggal,
      jam_mulai: item.jam_mulai,
      jam_selesai: item.jam_selesai,
      alasan: item.alasan,
      metode_hitung: item.metode_hitung || settings?.metode_hitung || 'manual',
      rate_per_jam: item.rate_per_jam || settings?.rate_tetap || 0,
    });
    setError(''); setShowModal(true);
  };

  const handleSave = async () => {
    setError('');
    if (!form.karyawan_id || !form.tanggal || !form.jam_mulai || !form.jam_selesai) {
      setError('Semua field harus diisi'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        karyawan_id: Number(form.karyawan_id),
        rate_per_jam: Number(form.rate_per_jam),
      };
      if (editing) {
        await updatePengajuanLembur(editing.id, payload);
      } else {
        await createPengajuanLembur(payload);
      }
      setShowModal(false); load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (item.status !== 'Menunggu') { alert('Pengajuan sudah diproses'); return; }
    if (!window.confirm(`Hapus pengajuan lembur ${item.nama} tanggal ${item.tanggal}?`)) return;
    try { await deletePengajuanLembur(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  // ===== Approve / Reject =====
  const handleApprove = async (id, nama) => {
    const catatan = prompt(`Catatan untuk ${nama} (opsional):`);
    try {
      await approvePengajuanLembur(id, { status: 'Disetujui', catatan_atasan: catatan || '' });
      load();
    } catch (err) { alert(err.response?.data?.error || 'Gagal approve'); }
  };

  const handleReject = async (id, nama) => {
    const alasan = prompt(`Alasan penolakan untuk ${nama}:`);
    if (!alasan) return;
    try {
      await approvePengajuanLembur(id, { status: 'Ditolak', catatan_atasan: alasan });
      load();
    } catch (err) { alert(err.response?.data?.error || 'Gagal menolak'); }
  };

  // ===== Settings =====
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updatePengaturanLembur({
        metode_hitung: settings.metode_hitung,
        rate_tetap: Number(settings.rate_tetap),
        aktif: true,
      });
      setShowSettings(false);
    } catch (err) {
      alert('Gagal menyimpan pengaturan');
    } finally { setSaving(false); }
  };

  // Calculate total hours
  const calcHours = (item) => {
    if (item.total_jam) return item.total_jam;
    if (!item.jam_mulai || !item.jam_selesai) return 0;
    const [sh, sm] = item.jam_mulai.split(':').map(Number);
    const [eh, em] = item.jam_selesai.split(':').map(Number);
    let total = (eh * 60 + em - sh * 60 - sm) / 60;
    if (total <= 0) total += 24;
    return Math.round(total * 100) / 100;
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Pengajuan Lembur</h2>
            <p className="page-desc">{stats?.total || 0} pengajuan di {selectedBulan}</p>
          </div>
          <div className="page-header-actions" style={{ gap: 8 }}>
            {hasMenuAction('pengajuan_lembur', 'edit') && (
              <button className="btn btn-secondary" onClick={() => setShowSettings(true)}>
                <Settings size={16} /><span>Pengaturan</span>
              </button>
            )}
            {hasMenuAction('pengajuan_lembur', 'create') && (
              <button className="btn btn-primary" onClick={openCreate}>
                <Plus size={16} /><span>Ajukan Lembur</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="dash-stats-row" style={{ marginBottom: 20 }}>
          <div className="dash-stat-col">
            <div className="stat-card"><div className="stat-card-body">
              <div className="stat-card-content">
                <h5 className="stat-card-label">Total</h5>
                <span className="stat-card-value" style={{ color: '#11cdef' }}>{stats.total}</span>
                <span className="stat-card-sublabel">pengajuan</span>
              </div>
              <div className="stat-card-icon-col">
                <div className="stat-icon-shape" style={{ background: '#e6fafc', color: '#11cdef' }}><Clock size={22} /></div>
              </div>
            </div></div>
          </div>
          <div className="dash-stat-col">
            <div className="stat-card"><div className="stat-card-body">
              <div className="stat-card-content">
                <h5 className="stat-card-label">Menunggu</h5>
                <span className="stat-card-value" style={{ color: '#fb6340' }}>{stats.menunggu}</span>
                <span className="stat-card-sublabel">perlu ACC</span>
              </div>
              <div className="stat-card-icon-col">
                <div className="stat-icon-shape" style={{ background: '#fef3c7', color: '#fb6340' }}><AlertCircle size={22} /></div>
              </div>
            </div></div>
          </div>
          <div className="dash-stat-col">
            <div className="stat-card"><div className="stat-card-body">
              <div className="stat-card-content">
                <h5 className="stat-card-label">Disetujui</h5>
                <span className="stat-card-value" style={{ color: '#2dce89' }}>{stats.disetujui}</span>
                <span className="stat-card-sublabel">{stats.total_jam || 0} jam</span>
              </div>
              <div className="stat-card-icon-col">
                <div className="stat-icon-shape" style={{ background: '#d1fae5', color: '#2dce89' }}><CheckCircle size={22} /></div>
              </div>
            </div></div>
          </div>
          <div className="dash-stat-col">
            <div className="stat-card"><div className="stat-card-body">
              <div className="stat-card-content">
                <h5 className="stat-card-label">Total Biaya</h5>
                <span className="stat-card-value" style={{ color: '#f5365c' }}>Rp {(stats.total_biaya || 0).toLocaleString('id-ID')}</span>
                <span className="stat-card-sublabel">lembur disetujui</span>
              </div>
              <div className="stat-card-icon-col">
                <div className="stat-icon-shape" style={{ background: '#fee2e2', color: '#f5365c' }}><DollarSign size={22} /></div>
              </div>
            </div></div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters">
        <div className="search-box"><Search size={18} /><input type="text" placeholder="Cari karyawan..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <input type="month" value={selectedBulan} onChange={e => setSelectedBulan(e.target.value)}
          style={{ padding: '8px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Semua Status</option>
          <option value="Menunggu">Menunggu</option>
          <option value="Disetujui">Disetujui</option>
          <option value="Ditolak">Ditolak</option>
        </select>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner-lg" /><p>Memuat data...</p></div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tanggal</th><th>NIK</th><th>Nama</th><th>Jam</th><th>Total Jam</th><th>Alasan</th>
                <th>Status</th><th>Biaya</th><th>Catatan</th>
                <th style={{ width: 100 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const totalJam = calcHours(d);
                return (
                  <tr key={d.id}>
                    <td>{d.tanggal}</td>
                    <td><code>{d.nik || '-'}</code></td>
                    <td><strong>{d.nama}</strong>
                      {d.jabatan && <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)', display: 'block' }}>{d.jabatan}</span>}
                    </td>
                    <td style={{ fontWeight: 600 }}>{d.jam_mulai?.slice(0,5)} - {d.jam_selesai?.slice(0,5)}</td>
                    <td style={{ fontWeight: 600 }}>{totalJam} jam</td>
                    <td style={{ fontSize: '0.8rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.alasan}</td>
                    <td><StatusBadge status={d.status} /></td>
                    <td style={{ fontWeight: 600, fontSize: '0.82rem' }}>
                      {d.total_biaya > 0 ? `Rp ${d.total_biaya.toLocaleString('id-ID')}` : '-'}
                    </td>
                    <td style={{ fontSize: '0.75rem', color: 'var(--gray-400)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.catatan_atasan || '-'}
                    </td>
                    <td>
                      <div className="action-btns" style={{ gap: 4 }}>
                        {d.status === 'Menunggu' && (
                          <>
                            {hasMenuAction('pengajuan_lembur', 'edit') && (
                              <>
                                <button className="btn-icon" style={{ color: '#2dce89' }} title="Setujui" onClick={() => handleApprove(d.id, d.nama)}>
                                  <ThumbsUp size={14} />
                                </button>
                                <button className="btn-icon btn-danger" title="Tolak" onClick={() => handleReject(d.id, d.nama)}>
                                  <ThumbsDown size={14} />
                                </button>
                              </>
                            )}
                            {hasMenuAction('pengajuan_lembur', 'edit') && (
                              <button className="btn-icon" title="Edit" onClick={() => openEdit(d)}><Edit2 size={14} /></button>
                            )}
                            {hasMenuAction('pengajuan_lembur', 'delete') && (
                              <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)}><Trash2 size={14} /></button>
                            )}
                          </>
                        )}
                        {d.status !== 'Menunggu' && (
                          <span style={{ fontSize: '0.7rem', color: 'var(--gray-300)' }}>-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center text-muted" style={{ padding: 40 }}>Belum ada pengajuan lembur.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Pengajuan' : 'Ajukan Lembur'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>Karyawan *</label>
                  <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: e.target.value})}>
                    <option value="">-- Pilih Karyawan --</option>
                    {karyawan.map(k => <option key={k.id} value={k.id}>{k.nik} - {k.nama}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tanggal *</label>
                  <input type="date" value={form.tanggal} onChange={e => setForm({...form, tanggal: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Metode Hitung</label>
                  <select value={form.metode_hitung} onChange={e => setForm({...form, metode_hitung: e.target.value})}>
                    <option value="manual">Manual</option>
                    <option value="rate_tetap">Rate Tetap/Jam</option>
                    <option value="rumus_gaji">Rumus Gaji (1/173)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Jam Mulai *</label>
                  <input type="time" value={form.jam_mulai} onChange={e => setForm({...form, jam_mulai: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Jam Selesai *</label>
                  <input type="time" value={form.jam_selesai} onChange={e => setForm({...form, jam_selesai: e.target.value})} />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>Alasan *</label>
                  <textarea value={form.alasan} onChange={e => setForm({...form, alasan: e.target.value})} rows={2}
                    style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem', resize: 'vertical' }}
                    placeholder="Alasan pengajuan lembur..." />
                </div>
                {form.metode_hitung !== 'manual' && (
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>Rate per Jam (Rp)</label>
                    <input type="number" value={form.rate_per_jam} onChange={e => setForm({...form, rate_per_jam: e.target.value})}
                      placeholder="Contoh: 25000" style={{ padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
                    <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                      {form.metode_hitung === 'rumus_gaji' ? 'Rumus: 1/173 × gaji bulanan × jam lembur' : 'Rate tetap per jam lembur'}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Ajukan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>Pengaturan Lembur</h3>
              <button className="btn-icon" onClick={() => setShowSettings(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Metode Perhitungan</label>
                <select value={settings.metode_hitung} onChange={e => setSettings({...settings, metode_hitung: e.target.value})}>
                  <option value="manual">Manual — Catat jam saja</option>
                  <option value="rate_tetap">Rate Tetap — Rp/jam tetap</option>
                  <option value="rumus_gaji">Rumus Gaji — 1/173 × gaji</option>
                </select>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Rate Tetap per Jam (Rp)</label>
                <input type="number" value={settings.rate_tetap} onChange={e => setSettings({...settings, rate_tetap: e.target.value})}
                  placeholder="25000" style={{ padding: '10px 14px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem', width: '100%' }} />
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 12 }}>
                Pengaturan ini akan digunakan sebagai default saat membuat pengajuan lembur baru.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowSettings(false)}>Tutup</button>
              <button className="btn btn-primary" onClick={handleSaveSettings} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
